import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { Appointment, AppointmentFormData, Patient, Fisioterapeuta } from '../../types/crud/appointments';

interface AppointmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: AppointmentFormData) => Promise<void>;
  appointment?: Appointment | null;
  patients: Patient[];
  fisioterapeutas: Fisioterapeuta[];
  initialDate?: Date;
}

export function AppointmentModal({
  isOpen,
  onClose,
  onSave,
  appointment,
  patients,
  fisioterapeutas,
  initialDate
}: AppointmentModalProps) {
  const [isLoading, setIsLoading] = useState(false);
  
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors }
  } = useForm<AppointmentFormData>();

  const watchStart = watch('start');

  useEffect(() => {
    if (isOpen) {
      if (appointment) {
        // Editar cita existente
        const startDate = new Date(appointment.start);
        const endDate = new Date(appointment.end);
        
        setValue('title', appointment.title);
        setValue('start', formatDateTimeLocal(startDate));
        setValue('end', formatDateTimeLocal(endDate));
        setValue('patientId', appointment.patientId);
        setValue('fisioterapeutaId', appointment.fisioterapeutaId);
        setValue('description', appointment.description || '');
        setValue('status', appointment.status);
        setValue('type', appointment.type);
        setValue('notes', appointment.notes || '');
      } else {
        // Nueva cita
        const now = initialDate || new Date();
        const startTime = new Date(now);
        const endTime = new Date(now);
        endTime.setHours(startTime.getHours() + 1);
        
        reset({
          title: '',
          start: formatDateTimeLocal(startTime),
          end: formatDateTimeLocal(endTime),
          patientId: '',
          fisioterapeutaId: '',
          description: '',
          status: 'PENDIENTE',
          type: 'evaluation',
          notes: ''
        });
      }
    }
  }, [isOpen, appointment, initialDate, setValue, reset]);

  // Actualizar automáticamente la hora de fin cuando cambia la de inicio
  useEffect(() => {
    if (watchStart) {
      const startDate = new Date(watchStart);
      const endDate = new Date(startDate);
      endDate.setHours(startDate.getHours() + 1);
      setValue('end', formatDateTimeLocal(endDate));
    }
  }, [watchStart, setValue]);

  const formatDateTimeLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const onSubmit = async (data: AppointmentFormData) => {
    setIsLoading(true);
    try {
      await onSave(data);
      onClose();
    } catch (error) {
      console.error('Error al guardar la cita:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        
        <div className="inline-block transform overflow-hidden rounded-lg bg-white dark:bg-gray-800 text-left align-bottom shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:align-middle">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-100">
                  {appointment ? 'Editar Cita' : 'Nueva Cita'}
                </h3>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                {/* Título */}
                <div>
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Título
                  </label>
                  <input
                    type="text"
                    id="title"
                    {...register('title', { required: 'El título es obligatorio' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Título de la cita"
                  />
                  {errors.title && (
                    <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
                  )}
                </div>

                {/* Paciente */}
                <div>
                  <label htmlFor="patientId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Paciente
                  </label>
                  <select
                    id="patientId"
                    {...register('patientId', { required: 'Debe seleccionar un paciente' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Seleccionar paciente</option>
                    {patients.map((patient) => (
                      <option key={patient.id} value={patient.id}>
                        {patient.firstName} {patient.lastName}
                      </option>
                    ))}
                  </select>
                  {errors.patientId && (
                    <p className="mt-1 text-sm text-red-600">{errors.patientId.message}</p>
                  )}
                </div>

                {/* Fisioterapeuta */}
                <div>
                  <label htmlFor="fisioterapeutaId" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fisioterapeuta
                  </label>
                  <select
                    id="fisioterapeutaId"
                    {...register('fisioterapeutaId', { required: 'Debe seleccionar un fisioterapeuta' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Seleccionar fisioterapeuta</option>
                    {fisioterapeutas.map((fisioterapeuta) => (
                      <option key={fisioterapeuta.id} value={fisioterapeuta.id}>
                        {fisioterapeuta.firstName} {fisioterapeuta.lastName} - {fisioterapeuta.specialty}
                      </option>
                    ))}
                  </select>
                  {errors.fisioterapeutaId && (
                    <p className="mt-1 text-sm text-red-600">{errors.fisioterapeutaId.message}</p>
                  )}
                </div>

                {/* Fecha y hora de inicio */}
                <div>
                  <label htmlFor="start" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fecha y hora de inicio
                  </label>
                  <input
                    type="datetime-local"
                    id="start"
                    {...register('start', { required: 'La fecha de inicio es obligatoria' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  {errors.start && (
                    <p className="mt-1 text-sm text-red-600">{errors.start.message}</p>
                  )}
                </div>

                {/* Fecha y hora de fin */}
                <div>
                  <label htmlFor="end" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Fecha y hora de fin
                  </label>
                  <input
                    type="datetime-local"
                    id="end"
                    {...register('end', { required: 'La fecha de fin es obligatoria' })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                  {errors.end && (
                    <p className="mt-1 text-sm text-red-600">{errors.end.message}</p>
                  )}
                </div>

                {/* Estado */}
                <div>
                  <label htmlFor="status" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Estado
                  </label>
                  <select
                    id="status"
                    {...register('status')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="PENDIENTE">Pendiente</option>
                    <option value="CONFIRMADA">Confirmada</option>
                    <option value="REALIZADA">Realizada</option>
                    <option value="CANCELADA">Cancelada</option>
                    <option value="NO_ACUDE">No acude</option>
                    <option value="REPROGRAMADA">Reprogramada</option>
                  </select>
                </div>

                {/* Tipo */}
                <div>
                  <label htmlFor="type" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Tipo de sesión
                  </label>
                  <select
                    id="type"
                    {...register('type')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="evaluation">Evaluación</option>
                    <option value="therapy">Terapia</option>
                    <option value="followup">Seguimiento</option>
                    <option value="rehabilitation">Rehabilitación</option>
                  </select>
                </div>

                {/* Descripción */}
                <div>
                  <label htmlFor="description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Descripción
                  </label>
                  <textarea
                    id="description"
                    rows={3}
                    {...register('description')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Descripción de la sesión"
                  />
                </div>

                {/* Notas */}
                <div>
                  <label htmlFor="notes" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Notas {watch('status') === 'CANCELADA' && <span className="text-xs text-red-500">(Motivo de cancelación)</span>}
                  </label>
                  <textarea
                    id="notes"
                    rows={2}
                    {...register('notes')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder={watch('status') === 'CANCELADA' ? 'Motivo de cancelación' : 'Notas adicionales'}
                  />
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
              <button
                type="submit"
                disabled={isLoading}
                className="inline-flex w-full justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Guardando...' : (appointment ? 'Actualizar' : 'Crear')}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-3 inline-flex w-full justify-center rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-4 py-2 text-base font-medium text-gray-700 dark:text-gray-300 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
} 